import { useEffect, useMemo, useState } from "react";

function debounce(fn, delay = 250) {
  let timer;

  const debounced = (...args) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => fn?.(...args), delay);
  };

  debounced.cancel = () => window.clearTimeout(timer);

  return debounced;
}

function formatCurrency(value, currency = "DKK") {
  return new Intl.NumberFormat(currency === "SEK" ? "sv-SE" : "da-DK", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function getRows(estimate = {}) {
  return [
    ...(estimate.materials || []).map((row) => ({
      ...row,
      rowKind: "material",
      kindLabel: "Material",
    })),
    ...(estimate.labour || []).map((row) => ({
      ...row,
      rowKind: "labour",
      kindLabel: "Labour",
    })),
    ...(estimate.extras || []).map((row) => ({
      ...row,
      rowKind: "extra",
      kindLabel: "Extra",
    })),
  ];
}

function getQuantity(row) {
  return row.rowKind === "labour"
    ? row.hours ?? row.quantity ?? 0
    : row.qty ?? row.quantity ?? 0;
}

function getUnitPrice(row) {
  return row.rowKind === "labour"
    ? row.hourlyRate ?? row.unitPrice ?? 0
    : row.price ?? row.unitPrice ?? 0;
}

function getUnit(row) {
  if (row.rowKind === "labour") return "hour";
  return row.unit || "unit";
}

function getDraftTotal(row) {
  return Number(getQuantity(row) || 0) * Number(getUnitPrice(row) || 0);
}

function isManual(row) {
  return Boolean(row?.manuallyEdited || row?.flags?.manuallyEdited);
}

function isInactive(row) {
  return Boolean(
    row?.inactive ||
      row?.excluded ||
      row?.flags?.inactive ||
      row?.flags?.excluded ||
      row?.meta?.inactive ||
      row?.meta?.excluded
  );
}

function getReason(row) {
  return (
    row?.inactiveReason ||
    row?.exclusionReason ||
    row?.flags?.inactiveReason ||
    row?.flags?.exclusionReason ||
    row?.meta?.inactiveReason ||
    row?.meta?.exclusionReason ||
    ""
  );
}

function getDefaultReason(rowKind) {
  if (rowKind === "material") return "Customer supplies material";
  if (rowKind === "labour") return "Labour not included";
  return "Not included in quote";
}

function inputClass(row, align = "left", width = "w-full") {
  const inactive = isInactive(row);

  return [
    width,
    "rounded-sm border px-2 py-1 text-sm outline-none focus:border-[#0f6cbd] focus:ring-1 focus:ring-[#0f6cbd]",
    align === "right" ? "text-right tabular-nums" : "",
    inactive
      ? "border-[#edebe9] bg-[#f3f2f1] text-[#605e5c] line-through"
      : isManual(row)
      ? "border-[#f3d6a0] bg-[#fff4ce] text-[#201f1e]"
      : "border-[#edebe9] bg-white text-[#201f1e]",
  ].join(" ");
}

export default function OverridesWorkspace({
  estimate,
  onUpdateEstimateRow,
  onSave,
  onRebuild,
  onUndo,
  canUndo = false,
  isBusy = false,
}) {
  const currency =
    estimate?.meta?.currency || estimate?.summary?.currency || "DKK";

  const rows = useMemo(() => getRows(estimate), [estimate]);
  const [draftRows, setDraftRows] = useState(rows);

  useEffect(() => {
    setDraftRows(rows);
  }, [rows]);

  const debouncedUpdateEstimateRow = useMemo(
    () => debounce(onUpdateEstimateRow, 250),
    [onUpdateEstimateRow]
  );

  useEffect(() => {
    return () => debouncedUpdateEstimateRow.cancel?.();
  }, [debouncedUpdateEstimateRow]);

  const manualCount = draftRows.filter(isManual).length;
  const inactiveCount = draftRows.filter(isInactive).length;

  const activeTotal = draftRows
    .filter((row) => !isInactive(row))
    .reduce((sum, row) => sum + getDraftTotal(row), 0);

  const inactiveTotal = draftRows
    .filter(isInactive)
    .reduce((sum, row) => sum + getDraftTotal(row), 0);

  const updateDraftRow = (rowId, rowKind, updates = {}) => {
    setDraftRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;

        const nextRow = {
          ...row,
          ...updates,
          manuallyEdited: true,
          flags: {
            ...(row.flags || {}),
            ...(updates.flags || {}),
            manuallyEdited: true,
          },
          meta: {
            ...(row.meta || {}),
            ...(updates.meta || {}),
          },
        };

        if (
          updates.quantity !== undefined ||
          updates.qty !== undefined ||
          updates.hours !== undefined
        ) {
          const quantity = Number(
            updates.quantity ?? updates.qty ?? updates.hours ?? 0
          );

          nextRow.quantity = quantity;

          if (rowKind === "labour") nextRow.hours = quantity;
          else nextRow.qty = quantity;
        }

        if (
          updates.unitPrice !== undefined ||
          updates.price !== undefined ||
          updates.rate !== undefined ||
          updates.hourlyRate !== undefined
        ) {
          const unitPrice = Number(
            updates.unitPrice ??
              updates.price ??
              updates.rate ??
              updates.hourlyRate ??
              0
          );

          nextRow.unitPrice = unitPrice;

          if (rowKind === "labour") {
            nextRow.hourlyRate = unitPrice;
            nextRow.rate = unitPrice;
          } else {
            nextRow.price = unitPrice;
          }
        }

        if (updates.unit !== undefined) {
          nextRow.unit = updates.unit;
        }

        if (updates.label !== undefined) {
          nextRow.label = updates.label;
          if (rowKind === "extra") nextRow.name = updates.label;
        }

        if (updates.excluded !== undefined || updates.inactive !== undefined) {
          const inactive = Boolean(updates.inactive ?? updates.excluded);

          nextRow.active = !inactive;
          nextRow.inactive = inactive;
          nextRow.excluded = inactive;

          nextRow.flags.active = !inactive;
          nextRow.flags.inactive = inactive;
          nextRow.flags.excluded = inactive;

          nextRow.meta.active = !inactive;
          nextRow.meta.inactive = inactive;
          nextRow.meta.excluded = inactive;

          const reason = inactive
            ? updates.inactiveReason ||
              updates.exclusionReason ||
              getReason(row) ||
              getDefaultReason(rowKind)
            : "";

          nextRow.inactiveReason = reason;
          nextRow.exclusionReason = reason;
          nextRow.flags.inactiveReason = reason;
          nextRow.flags.exclusionReason = reason;
          nextRow.meta.inactiveReason = reason;
          nextRow.meta.exclusionReason = reason;
        }

        if (
          updates.inactiveReason !== undefined ||
          updates.exclusionReason !== undefined
        ) {
          const reason = updates.inactiveReason ?? updates.exclusionReason;

          nextRow.inactiveReason = reason;
          nextRow.exclusionReason = reason;
          nextRow.flags.inactiveReason = reason;
          nextRow.flags.exclusionReason = reason;
          nextRow.meta.inactiveReason = reason;
          nextRow.meta.exclusionReason = reason;
        }

        nextRow.total = getDraftTotal(nextRow);

        return nextRow;
      })
    );

    debouncedUpdateEstimateRow(rowId, rowKind, updates);
  };

  const toggleInactive = (row) => {
    const inactive = !isInactive(row);
    const reason = inactive ? getReason(row) || getDefaultReason(row.rowKind) : "";

    updateDraftRow(row.id, row.rowKind, {
      inactive,
      excluded: inactive,
      inactiveReason: reason,
      exclusionReason: reason,
    });
  };

  if (!draftRows.length) {
    return (
      <div className="h-full bg-[#f3f2f1] p-4">
        <div className="rounded-sm border border-[#edebe9] bg-white px-4 py-3 text-sm text-[#605e5c] shadow-sm">
          No rows available.
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-[#f3f2f1] p-4 text-[#201f1e]">
      <section className="overflow-hidden rounded-sm border border-[#edebe9] bg-white shadow-sm">
        <div className="flex h-11 items-center justify-between border-b border-[#edebe9] bg-[#faf9f8] px-3">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onSave?.()}
              disabled={isBusy || !onSave}
              className="rounded-sm px-3 py-1.5 text-sm font-medium text-[#201f1e] hover:bg-[#f3f2f1] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Save changes
            </button>

            <button
              type="button"
              onClick={() => onRebuild?.()}
              disabled={isBusy || !onRebuild}
              className="rounded-sm px-3 py-1.5 text-sm text-[#323130] hover:bg-[#f3f2f1] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Rebuild
            </button>

            <button
              type="button"
              onClick={() => onUndo?.()}
              disabled={isBusy || !canUndo}
              className="rounded-sm px-3 py-1.5 text-sm text-[#323130] hover:bg-[#f3f2f1] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Undo
            </button>
          </div>

          <div className="flex items-center gap-2 text-xs text-[#605e5c]">
            <span className="rounded-sm border border-[#edebe9] bg-white px-2 py-1">
              {manualCount} manual
            </span>
            <span className="rounded-sm border border-[#edebe9] bg-white px-2 py-1">
              {inactiveCount} inactive
            </span>
            <span className="rounded-sm border border-[#edebe9] bg-white px-2 py-1">
              Active {formatCurrency(activeTotal, currency)}
            </span>
            {inactiveCount > 0 && (
              <span className="rounded-sm border border-[#edebe9] bg-white px-2 py-1">
                Inactive {formatCurrency(inactiveTotal, currency)}
              </span>
            )}
          </div>
        </div>

        <div className="border-b border-[#edebe9] bg-white px-4 py-3">
          <h2 className="text-sm font-semibold text-[#201f1e]">
            Manual adjustments
          </h2>
          <p className="mt-0.5 text-xs text-[#605e5c]">
            Edit row values and activate or deactivate rows included in the quote.
          </p>
        </div>

        <div className="overflow-auto">
          <table className="min-w-full border-collapse text-sm">
            <thead className="bg-[#f3f2f1]">
              <tr className="border-b border-[#edebe9]">
                <HeaderCell>Active</HeaderCell>
                <HeaderCell>Type</HeaderCell>
                <HeaderCell>Label</HeaderCell>
                <HeaderCell align="right">Quantity</HeaderCell>
                <HeaderCell>Unit</HeaderCell>
                <HeaderCell align="right">Unit price</HeaderCell>
                <HeaderCell align="right">Total excl. VAT</HeaderCell>
                <HeaderCell>Status / note</HeaderCell>
              </tr>
            </thead>

            <tbody>
              {draftRows.map((row) => {
                const inactive = isInactive(row);
                const quantity = getQuantity(row);
                const unitPrice = getUnitPrice(row);

                return (
                  <tr
                    key={row.id}
                    className={[
                      "border-b border-[#edebe9] hover:bg-[#f3f2f1]",
                      inactive ? "bg-[#faf9f8] text-[#605e5c]" : "bg-white",
                    ].join(" ")}
                  >
                    <td className="w-16 px-3 py-2 align-top">
                      <input
                        type="checkbox"
                        checked={!inactive}
                        onChange={() => toggleInactive(row)}
                        disabled={isBusy}
                        className="h-4 w-4 border-[#605e5c]"
                        title={inactive ? "Activate row" : "Deactivate row"}
                      />
                    </td>

                    <td className="w-28 px-3 py-2 align-top">
                      <div className="text-sm text-[#323130]">{row.kindLabel}</div>

                      <div className="mt-1 flex flex-col items-start gap-1">
                        {isManual(row) && (
                          <span className="rounded-sm border border-[#f3d6a0] bg-[#fff4ce] px-1.5 py-0.5 text-[10px] font-medium text-[#8a6d1d]">
                            Manual
                          </span>
                        )}

                        {inactive && (
                          <span className="rounded-sm border border-[#cfe4fa] bg-[#eaf3ff] px-1.5 py-0.5 text-[10px] font-medium text-[#0f6cbd]">
                            Inactive
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="min-w-[260px] px-3 py-2 align-top">
                      <input
                        value={row.label || row.name || ""}
                        onChange={(e) =>
                          updateDraftRow(row.id, row.rowKind, {
                            label: e.target.value,
                          })
                        }
                        disabled={isBusy}
                        className={inputClass(row)}
                      />
                    </td>

                    <td className="w-28 px-3 py-2 align-top">
                      <input
                        type="number"
                        value={quantity}
                        onChange={(e) =>
                          updateDraftRow(row.id, row.rowKind, {
                            quantity: Number(e.target.value),
                          })
                        }
                        disabled={isBusy}
                        className={inputClass(row, "right", "w-24")}
                      />
                    </td>

                    <td className="w-24 px-3 py-2 align-top">
                      {row.rowKind === "labour" ? (
                        <div className="rounded-sm border border-[#edebe9] bg-[#f3f2f1] px-2 py-1 text-sm text-[#605e5c]">
                          hour
                        </div>
                      ) : (
                        <input
                          value={getUnit(row)}
                          onChange={(e) =>
                            updateDraftRow(row.id, row.rowKind, {
                              unit: e.target.value,
                            })
                          }
                          disabled={isBusy}
                          className={inputClass(row, "left", "w-20")}
                        />
                      )}
                    </td>

                    <td className="w-32 px-3 py-2 align-top">
                      <input
                        type="number"
                        value={unitPrice}
                        onChange={(e) =>
                          updateDraftRow(row.id, row.rowKind, {
                            unitPrice: Number(e.target.value),
                          })
                        }
                        disabled={isBusy}
                        className={inputClass(row, "right", "w-28")}
                      />
                    </td>

                    <td className="w-36 px-3 py-2 text-right align-top font-medium tabular-nums text-[#323130]">
                      <span className={inactive ? "line-through" : ""}>
                        {formatCurrency(getDraftTotal(row), currency)}
                      </span>
                      <div className="mt-0.5 text-[11px] font-normal text-[#605e5c]">
                        {inactive ? "inactive" : "excl. VAT"}
                      </div>
                    </td>

                    <td className="min-w-[260px] px-3 py-2 align-top">
                      <input
                        value={inactive ? getReason(row) : row.meta?.notes || ""}
                        onChange={(e) =>
                          inactive
                            ? updateDraftRow(row.id, row.rowKind, {
                                inactiveReason: e.target.value,
                                exclusionReason: e.target.value,
                              })
                            : updateDraftRow(row.id, row.rowKind, {
                                meta: {
                                  ...(row.meta || {}),
                                  notes: e.target.value,
                                },
                              })
                        }
                        disabled={isBusy}
                        placeholder={inactive ? "Reason / note" : "Internal note"}
                        className={inputClass(row)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function HeaderCell({ children, align = "left" }) {
  return (
    <th
      className={[
        "border-r border-[#edebe9] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[#605e5c] last:border-r-0",
        align === "right" ? "text-right" : "text-left",
      ].join(" ")}
    >
      {children}
    </th>
  );
}